import { adminApi } from '@/features/admin';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Card, CardContent } from '@/shared/ui/card';
import { Skeleton } from '@/shared/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle, Eye, MessageSquare, XCircle } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

export default function AdminQuestionsPage() {
  const [page, setPage] = useState(1);
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['admin-questions', page],
    queryFn: () => adminApi.getQuestionsToModerate(page, 20),
  });

  const validateMutation = useMutation({
    mutationFn: (questionId: number) => adminApi.validateQuestion(questionId),
    onSuccess: () => {
      toast.success('Question validee');
      queryClient.invalidateQueries({ queryKey: ['admin-questions'] });
    },
    onError: () => toast.error('Erreur lors de la validation'),
  });

  const deleteMutation = useMutation({
    mutationFn: (questionId: number) => adminApi.deleteQuestion(questionId),
    onSuccess: () => {
      toast.success('Question supprimee');
      queryClient.invalidateQueries({ queryKey: ['admin-questions'] });
    },
    onError: () => toast.error('Erreur lors de la suppression'),
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Moderation des questions</h1>
        <Badge variant="outline">
          <MessageSquare className="h-3 w-3 mr-1" />
          {data?.total || 0} question(s) en attente
        </Badge>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-4 space-y-4">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : data?.questions.length ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>#</TableHead>
                  <TableHead>Utilisateur</TableHead>
                  <TableHead className="w-1/2">Contenu</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.questions.map((question, index) => (
                  <TableRow key={question.questionId}>
                    <TableCell>{(page - 1) * 20 + index + 1}</TableCell>
                    <TableCell>
                      <Link
                        to={`/admin/users/${question.nodeId}`}
                        className="hover:underline font-medium"
                      >
                        {question.username}
                      </Link>
                    </TableCell>
                    <TableCell className="max-w-md">
                      <p className="text-sm whitespace-pre-wrap break-words">
                        {question.description}
                      </p>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button variant="ghost" size="icon" asChild>
                          <Link to={`/admin/users/${question.nodeId}`}>
                            <Eye className="h-4 w-4" />
                          </Link>
                        </Button>
                        <Button
                          className="bg-green-500 hover:bg-green-600"
                          size="sm"
                          onClick={() => validateMutation.mutate(question.questionId)}
                          disabled={validateMutation.isPending}
                        >
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Valider
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => deleteMutation.mutate(question.questionId)}
                          disabled={deleteMutation.isPending}
                        >
                          <XCircle className="h-4 w-4 mr-1" />
                          Effacer
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="p-8 text-center text-muted-foreground">
              <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <h2 className="text-xl font-semibold mb-2">Aucune question en attente</h2>
              <p>Toutes les questions ont ete moderees.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {data && data.total > 20 && (
        <div className="flex justify-center gap-2">
          <Button
            variant="outline"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            Precedent
          </Button>
          <span className="flex items-center px-4">
            Page {page} / {Math.ceil(data.total / 20)}
          </span>
          <Button
            variant="outline"
            onClick={() => setPage((p) => p + 1)}
            disabled={page >= Math.ceil(data.total / 20)}
          >
            Suivant
          </Button>
        </div>
      )}
    </div>
  );
}
